import { useMemo, useRef, useState } from 'react'
import Datetime from 'utils/datetime/datetime'
import { getUtcHourStart } from 'utils/datetime/dayTimeSteps'
import { isValidPriceValue } from '../../IntradayTradingView.helpers'
import type { HourInput } from '../../IntradayTradingView.types'
import { createIntradayHourLabel } from '../intradayHour.helper'

const DEFAULT_INTRADAY_PRICE_ROWS = 12

function createPriceInput(id: number): HourInput {
  return {
    id,
    value: '',
  }
}

function createInitialPriceInputs(): HourInput[] {
  return Array.from({ length: DEFAULT_INTRADAY_PRICE_ROWS }, (_unused, index) => createPriceInput(index))
}

function getNextPriceBaseStartTime(): Date {
  return new Date(Datetime.addHours(getUtcHourStart(Date.now()), 1))
}

type UsePriceInputRowsResult = {
  inputPrices: HourInput[]
  hasInvalidPriceInputs: boolean
  priceHourLabels: string[]
  resetPriceInputs: () => void
  handlePriceInputChange: (id: number, value: string) => void
  handleAddPriceRow: () => void
  handleRemoveLastPriceRow: () => void
}

export function usePriceInputRows(): UsePriceInputRowsResult {
  const [inputPrices, setInputPrices] = useState<HourInput[]>(() => createInitialPriceInputs())
  const nextPriceInputId = useRef(DEFAULT_INTRADAY_PRICE_ROWS)

  const hasInvalidPriceInputs = useMemo(
    () => inputPrices.some((price) => price.value.trim() !== '' && !isValidPriceValue(price.value)),
    [inputPrices]
  )
  const priceHourLabels = useMemo(() => {
    const priceBaseStartTime = getNextPriceBaseStartTime()
    return inputPrices.map((_row, index) => createIntradayHourLabel(index, priceBaseStartTime))
  }, [inputPrices])

  function handlePriceInputChange(id: number, value: string): void {
    setInputPrices((previous) => previous.map((item) => (item.id === id ? { ...item, value } : item)))
  }

  function resetPriceInputs(): void {
    setInputPrices((previous) => previous.map((price) => ({ ...price, value: '' })))
  }

  function handleAddPriceRow(): void {
    setInputPrices((previous) => [...previous, createPriceInput(nextPriceInputId.current++)])
  }

  function handleRemovePriceRow(id: number): void {
    setInputPrices((previous) => previous.filter((item) => item.id !== id))
  }

  function handleRemoveLastPriceRow(): void {
    const lastPriceRow = inputPrices[inputPrices.length - 1]
    if (!lastPriceRow) return
    handleRemovePriceRow(lastPriceRow.id)
  }

  return {
    inputPrices,
    hasInvalidPriceInputs,
    priceHourLabels,
    resetPriceInputs,
    handlePriceInputChange,
    handleAddPriceRow,
    handleRemoveLastPriceRow,
  }
}
